
import { useEffect, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import PageTransition from "@/components/PageTransition";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase, getUserIdForRecommendation, ensureUserExists } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { ExternalLink, ThumbsDown, ThumbsUp } from "lucide-react";
import type { RecommendationItem } from "@/types/recommendation";
import { toast } from "sonner";
import LoginRequired from "@/components/LoginRequired";

const Recommended = () => {
  // 1. Set up proper session handling
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [currentAuthUserId, setCurrentAuthUserId] = useState<string | null>(null);
  const [mappedUserId, setMappedUserId] = useState<string | null>(null);
  const [userIdError, setUserIdError] = useState<Error | null>(null);
  const queryClient = useQueryClient();

  // Create memoized function to get user ID
  const fetchUserMapping = useCallback(async (authUserId: string) => {
    try {
      // First attempt to get the mapping
      let userIdForRecs = await getUserIdForRecommendation(authUserId);
      
      // If no mapping exists, try to create one, but only once
      if (!userIdForRecs) {
        console.log("No existing user mapping, attempting to create one");
        userIdForRecs = await ensureUserExists(authUserId);
      }
      
      return userIdForRecs;
    } catch (err) {
      console.error("Error in user mapping:", err);
      setUserIdError(err instanceof Error ? err : new Error(String(err)));
      return null;
    }
  }, []);

  // Fetch current session on component mount
  useEffect(() => {
    let isMounted = true;
    
    const fetchSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        console.log("Initial session check:", data.session);
        
        if (data.session?.user?.id && isMounted) {
          const authUserId = data.session.user.id;
          setCurrentAuthUserId(authUserId);
          
          // Try to map the auth ID to a user ID for recommendations
          const userIdForRecs = await fetchUserMapping(authUserId);
          
          if (userIdForRecs && isMounted) {
            console.log("Using user ID for recommendations:", userIdForRecs);
            setMappedUserId(userIdForRecs);
          } else if (isMounted) {
            console.warn("Could not map or create user ID");
          }
        }
      } catch (err) {
        console.error("Error fetching session:", err);
        toast.error("Failed to check login status");
      } finally {
        if (isMounted) {
          setIsLoadingInitial(false);
        }
      }
    };

    fetchSession();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;
      
      console.log("Auth state changed:", event, session);
      const newAuthUserId = session?.user?.id || null;
      setCurrentAuthUserId(newAuthUserId);
      
      // Reset error state on auth changes
      setUserIdError(null);
      
      if (newAuthUserId) {
        // Try to map the auth ID to a user ID whenever auth state changes
        const newMappedUserId = await fetchUserMapping(newAuthUserId);
        
        if (isMounted) {
          setMappedUserId(newMappedUserId);
          
          // Invalidate recommendations on auth change - FIXED: Using object with queryKey property
          if (newMappedUserId) {
            queryClient.invalidateQueries({ queryKey: ['recommendations', newMappedUserId] });
          }
        }
      } else if (isMounted) {
        setMappedUserId(null);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [fetchUserMapping, queryClient]);

  // 2. Query for recommendations using the mapped user ID
  const { data: recommendations = [], isLoading: isLoadingRecommendations, error } = useQuery({
    queryKey: ['recommendations', mappedUserId || currentAuthUserId],
    queryFn: async () => {
      // Prefer mapped user ID, fall back to auth user ID
      const userId = mappedUserId || currentAuthUserId;
      
      if (!userId) {
        console.log("No user ID available for recommendations query");
        return [];
      }
      
      console.log("Fetching recommendations for user:", userId);
      const { data, error } = await supabase.rpc('fetch_recommendation_service', {
        p_user_id: userId
      });
      
      if (error) {
        console.error("Error fetching recommendations:", error);
        toast.error("Failed to load recommendations");
        throw error;
      }
      
      console.log("Raw recommendation data:", data);
      
      // Ensure we return an array of recommendation items
      if (Array.isArray(data)) {
        return data as RecommendationItem[];
      }
      return [];
    },
    // Only enable the query when there's a user ID and initial loading is complete
    enabled: Boolean((mappedUserId || currentAuthUserId) && !isLoadingInitial && !userIdError),
    staleTime: 60 * 1000, // Cache for 1 minute
    refetchOnWindowFocus: false, // Don't refetch on focus
    retry: 1, // Minimal retry
  });

  // Handle errors
  useEffect(() => {
    if (error) {
      console.error("Query error:", error);
      toast.error("Could not load recommendations");
    }
  }, [error]);

  // Check if we have recommendations to display
  const hasRecommendations = recommendations.length > 0;
  console.log("Has recommendations:", hasRecommendations, "Count:", recommendations.length);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  // Show loading state during initial authentication check
  if (isLoadingInitial) {
    return (
      <PageTransition>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="space-y-8">
        <section className="text-center max-w-3xl mx-auto">
          <div className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Recommended Items
          </div>
          <h1 className="text-4xl font-bold tracking-tight">
            Discover Products Tailored for You
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            PricePal analyzes your preferences to recommend the best products at optimal prices.
          </p>
        </section>

        {isLoadingRecommendations ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        ) : error ? (
          <Card>
            <CardHeader>
              <CardTitle>Error Loading Recommendations</CardTitle>
              <CardDescription>
                We encountered an error fetching your recommendations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-destructive">Please try again later.</p>
            </CardContent>
          </Card>
        ) : !currentAuthUserId ? (
          <LoginRequired />
        ) : !mappedUserId ? (
          <Card>
            <CardHeader>
              <CardTitle>Account Setup</CardTitle>
              <CardDescription>
                Your Discord account needs to be linked to a user profile.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex flex-col items-center justify-center gap-4 bg-secondary/50 rounded-md p-4">
                <p className="text-muted-foreground text-center">Please login again to complete your account setup</p>
              </div>
            </CardContent>
          </Card>
        ) : !hasRecommendations ? (
          <Card>
            <CardHeader>
              <CardTitle>No Recommendations Yet</CardTitle>
              <CardDescription>
                Make your first query to get personal recommendations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-48 flex flex-col items-center justify-center gap-4 bg-secondary/50 rounded-md p-4">
                <p className="text-muted-foreground text-center">Your recommendations will appear here after you make a query</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {recommendations.map((item: RecommendationItem) => (
              <Card key={item.id} className="flex flex-col">
                <CardHeader>
                  <CardTitle className="line-clamp-2">{item.item_name}</CardTitle>
                  <CardDescription>
                    <span className="font-medium">{item.vendor}</span>
                  </CardDescription>
                </CardHeader>
                <CardContent className="flex-grow">
                  <div className="space-y-4">
                    <div className="text-2xl font-bold text-primary">
                      {formatPrice(item.price)}
                    </div>
                    {item.metadata?.description && (
                      <p className="text-sm text-muted-foreground line-clamp-3">
                        {item.metadata.description}
                      </p>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between border-t pt-4">
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon">
                      <ThumbsUp className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="icon">
                      <ThumbsDown className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button variant="outline" size="sm" asChild>
                    <a href={item.link} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="mr-2 h-4 w-4" />
                      View Item
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default Recommended;
